import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";
import { KeyReveal } from "../CompanySettings/KeyReveal";

/** Super Admin → firma: paketi Kasa + L-PFR (dodela i uklanjanje). */
export function KasaPaketiAdmin({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [kind, setKind] = useState<"OBICNA" | "HORECA">("OBICNA");
  const [label, setLabel] = useState("");
  const list = useQuery({ queryKey: ["admin-kasa-paketi", companyId], queryFn: () => api.adminListKasaPaketi(companyId) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-kasa-paketi", companyId] });
  const create = useMutation({
    mutationFn: () => api.adminCreateKasaPaket({ companyId, kind, label: label.trim() || undefined }),
    onSuccess: () => {
      setLabel("");
      refresh();
      // Fakturisanje ide kroz Ponude (cene i stavke se biraju tamo)
      if (window.confirm("L-PFR paket je dodeljen.\n\nDa li ga fakturišeš? OK = otvori Ponude da napraviš ponudu/fakturu, Cancel = ne fakturiši (besplatno ili već plaćeno).")) {
        navigate("/finora-admin/ponude");
      }
    },
  });
  const lic = useQuery({ queryKey: ["admin-company-licences", companyId], queryFn: () => api.adminCompanyLicences(companyId), retry: false });
  const revoke = useMutation({ mutationFn: (id: string) => api.adminRevokeKasaPaket(id), onSuccess: refresh });

  const bind = useMutation({
    mutationFn: (v: { id: string; jid: string | null }) => api.adminBindKasaPaketJid(v.id, v.jid),
    onSuccess: refresh,
    onError: (e) => window.alert(e instanceof Error ? e.message : "Vezivanje nije uspelo."),
  });
  const knownJids = (lic.data?.installations ?? []).map((i) => i.jid).filter((j): j is string => !!j);
  const askJid = (id: string, current: string | null) => {
    const used = new Set((list.data ?? []).map((x) => x.jid).filter(Boolean));
    const hint = current ?? knownJids.find((j) => !used.has(j)) ?? "";
    const v = window.prompt(`JID za ovaj paket (prazno = oslobodi mesto).${knownJids.length ? `\nPoznati JID-ovi: ${knownJids.join(", ")}` : ""}`, hint);
    if (v !== null) bind.mutate({ id, jid: v.trim() || null });
  };

  return (
    <div className="space-y-2 rounded-xl border border-border bg-surface p-4">
      <div>
        <h2 className="text-sm font-semibold text-slate-800">Paketi Kasa + L-PFR</h2>
        <p className="text-xs text-slate-400">Jedan paket = jedna kasa + jedan L-PFR + jedan JID + jedna lokacija. Vrsta je obična ili HoReCa.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value as "OBICNA" | "HORECA")} className="h-9 rounded-lg border border-border px-2 text-sm">
          <option value="OBICNA">Obična kasa</option>
          <option value="HORECA">HoReCa kasa</option>
        </select>
        <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Naziv (neobavezno)" className="h-9 w-56 rounded-lg border border-border px-2 text-sm" />
        <Button size="sm" disabled={create.isPending} onClick={() => create.mutate()}>
          Dodeli paket (12 meseci)
        </Button>
      </div>
      {create.isError && <p className="text-sm text-red-600">Dodela nije uspela.</p>}
      {(list.data ?? []).length === 0 ? (
        <p className="text-sm text-slate-400">Firma nema dodeljenih paketa.</p>
      ) : (
        <ul className="divide-y divide-border text-sm">
          {(list.data ?? []).map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2 py-1.5">
              <span>
                {p.label || "Bez naziva"} · {p.kind === "HORECA" ? "HoReCa" : "Obična"} · JID: {p.jid ?? "čeka karticu"}
                {p.locationName ? ` · ${p.locationName}` : ""}
                {p.activeUntil ? ` · do ${new Date(p.activeUntil).toLocaleDateString("sr-RS")}` : ""}
              </span>
              <button className="mr-3 text-xs text-brand-600 hover:underline" onClick={() => askJid(p.id, p.jid)}>
                {p.jid ? "Promeni JID" : "Veži JID"}
              </button>
              <button className="text-xs text-slate-400 hover:text-red-600" onClick={() => window.confirm("Ukloniti paket?") && revoke.mutate(p.id)}>
                Ukloni
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="space-y-2 border-t border-border pt-3">
        <h3 className="text-sm font-semibold text-slate-800">Licence firme</h3>
        {lic.isLoading ? (
          <p className="text-sm text-slate-400">Učitavanje...</p>
        ) : !lic.data ? (
          <p className="text-sm text-slate-400">Licence nisu dostupne.</p>
        ) : (
          <>
            <div>
              <p className="text-xs font-medium text-slate-500">L-PFR licence</p>
              {lic.data.lpfr.length === 0 ? (
                <p className="text-sm text-slate-400">Nema L-PFR licenci.</p>
              ) : (
                lic.data.lpfr.map((k) => (
                  <div key={k.id} className="text-sm">
                    {k.label || "L-PFR licenca"} <span className="text-xs text-slate-400">· napravljena {new Date(k.created_at).toLocaleDateString("sr-RS")}</span>
                    <KeyReveal value={k.key} />
                  </div>
                ))
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">Licence kase</p>
              {lic.data.kasa.length === 0 ? (
                <p className="text-sm text-slate-400">Nema licenci kase.</p>
              ) : (
                lic.data.kasa.map((k) => (
                  <div key={k.id} className="text-sm">
                    {k.label || "Kasa"} <span className="text-xs text-slate-400">· napravljena {new Date(k.created_at).toLocaleDateString("sr-RS")}</span>
                    <KeyReveal value={k.key} />
                  </div>
                ))
              )}
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500">L-PFR kartice koje su potpisale račune</p>
              {lic.data.installations.length === 0 ? (
                <p className="text-sm text-slate-400">Još nijedan račun nije potpisan preko L-PFR-a.</p>
              ) : (
                <ul className="text-sm text-slate-700">
                  {lic.data.installations.map((i) => (
                    <li key={i.jid ?? "-"}>
                      JID {i.jid ?? "nepoznat"} · {i.invoices} računa · poslednji {new Date(i.last_at).toLocaleDateString("sr-RS")}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
